import React from 'react'
import { apiPost } from '../../../lib/api'
import { SERVICES_IMPORT, SERVICES_IMPORT_PREVIEW } from '../../../lib/endpoints'

type PreviewRequest = {
  columns: string[]
  mapping: Record<string, string>
  rows: Array<Record<string, string>>
}

type PreviewResponse = {
  valid: boolean
  totalRows: number
  missingRequiredFields: string[]
  warnings: string[]
  rows: Array<{
    rowNumber: number
    name: string
    offeringType: string
    description: string | null
    price: string | null
    durationMinutes: string | null
    rowWarnings: string[]
  }>
}

type ImportResult = {
  importedCount: number
  skippedCount: number
  errors: string[]
}

const REQUIRED_TARGETS = ['name', 'price'] as const
const TARGET_OPTIONS = [
  { value: 'name', label: 'name' },
  { value: 'type', label: 'type — service or product' },
  { value: 'description', label: 'description (optional)' },
  { value: 'price', label: 'price' },
  { value: 'duration_minutes', label: 'duration_minutes — services only (optional)' },
  { value: 'skip', label: 'skip column' },
]

function splitCsvLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i++
      } else {
        inQuotes = !inQuotes
      }
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  result.push(current.trim())
  return result
}

function isGuidanceRow(line: string): boolean {
  return (splitCsvLine(line)[0] ?? '').startsWith('#')
}

function parseCsv(content: string): { columns: string[]; rows: Array<Record<string, string>> } {
  const lines = content.split(/\r?\n/).map(l => l.trim()).filter(Boolean)
  if (lines.length < 2) return { columns: [], rows: [] }
  const columns = splitCsvLine(lines[0])
  const rows = lines
    .slice(1)
    .filter(line => !isGuidanceRow(line))
    .map(line => {
      const values = splitCsvLine(line)
      return columns.reduce<Record<string, string>>((acc, col, i) => {
        acc[col] = values[i] ?? ''
        return acc
      }, {})
    })
  return { columns, rows }
}

function detectTarget(col: string): string {
  const key = col.toLowerCase().replace(/\s+/g, '_')
  if (key === 'name' || key === 'service_name' || key === 'offering_name') return 'name'
  if (key === 'type' || key === 'offering_type' || key === 'kind') return 'type'
  if (key === 'description' || key === 'desc') return 'description'
  if (key === 'price' || key === 'cost' || key === 'amount') return 'price'
  if (key === 'duration_minutes' || key === 'duration' || key === 'minutes' || key === 'duration_mins') return 'duration_minutes'
  return 'skip'
}

export function ServiceImportWizard({
  open,
  onClose,
  onImported,
}: {
  open: boolean
  onClose: () => void
  onImported: () => void
}) {
  const [step, setStep] = React.useState(1)
  const [fileName, setFileName] = React.useState('')
  const [columns, setColumns] = React.useState<string[]>([])
  const [rows, setRows] = React.useState<Array<Record<string, string>>>([])
  const [mapping, setMapping] = React.useState<Record<string, string>>({})
  const [preview, setPreview] = React.useState<PreviewResponse | null>(null)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [result, setResult] = React.useState<ImportResult | null>(null)
  const [dropActive, setDropActive] = React.useState(false)
  const [guideOpen, setGuideOpen] = React.useState(true)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const dragDepthRef = React.useRef(0)

  React.useEffect(() => {
    if (!open) {
      setStep(1)
      setFileName('')
      setColumns([])
      setRows([])
      setMapping({})
      setPreview(null)
      setBusy(false)
      setError(null)
      setResult(null)
      setDropActive(false)
      dragDepthRef.current = 0
    }
  }, [open])

  if (!open) return null

  function downloadTemplate() {
    const csvRows = [
      ['name', 'type', 'description', 'price', 'duration_minutes'],
      [
        '# REQUIRED: Name of the service or product',
        '# REQUIRED: "service" or "product" (defaults to service if blank)',
        '# Optional: Short description',
        '# REQUIRED: Price — no currency symbol (e.g. 65.00)',
        '# Optional: Duration in minutes — for services only (e.g. 60)',
      ],
      ['Haircut & Style', 'service', 'Full haircut and blow-dry', '65.00', '60'],
      ['Deep Tissue Massage', 'service', '60-minute deep tissue massage', '90.00', '60'],
      ['Consultation', 'service', 'Initial consultation', '45.00', '30'],
      ['Moroccan Oil Shampoo', 'product', 'Take-home nourishing shampoo 250ml', '28.00', ''],
      ['Keratin Treatment Kit', 'product', 'At-home keratin maintenance kit', '55.00', ''],
    ]
    const csv = csvRows.map(r => r.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'offerings_template.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const requiredMissing = REQUIRED_TARGETS.filter(target => !Object.values(mapping).includes(target))

  function isCsvFile(file: File): boolean {
    const name = file.name.toLowerCase()
    if (name.endsWith('.csv')) return true
    const t = (file.type || '').toLowerCase()
    return t === 'text/csv' || t === 'application/csv' || t === 'application/vnd.ms-excel' || t === 'text/plain'
  }

  async function handleFile(file: File) {
    const text = await file.text()
    const parsed = parseCsv(text)
    const autoMapping = parsed.columns.reduce<Record<string, string>>((acc, col) => {
      acc[col] = detectTarget(col)
      return acc
    }, {})
    setFileName(file.name)
    setColumns(parsed.columns)
    setRows(parsed.rows)
    setMapping(autoMapping)
    setPreview(null)
    setResult(null)
    setError(null)
    setStep(2)
  }

  async function runPreview() {
    setBusy(true)
    setError(null)
    try {
      const payload: PreviewRequest = { columns, rows, mapping }
      const res = await apiPost<PreviewResponse, PreviewRequest>(SERVICES_IMPORT_PREVIEW, payload)
      setPreview(res)
      setStep(3)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Preview failed')
    } finally {
      setBusy(false)
    }
  }

  async function runImport() {
    setBusy(true)
    setError(null)
    try {
      const payload = { payload: { columns, rows, mapping } }
      const res = await apiPost<ImportResult, typeof payload>(SERVICES_IMPORT, payload)
      setResult(res)
      setStep(4)
      onImported()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Import failed')
    } finally {
      setBusy(false)
    }
  }

  function goBack() {
    if (busy) return
    setError(null)
    if (step === 2) setStep(1)
    else if (step === 3) { setPreview(null); setStep(2) }
    else if (step === 4) setStep(3)
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 90, padding: 24, overflow: 'auto' }}>
      <div className="panel" style={{ maxWidth: 900, margin: '0 auto', padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <div style={{ fontSize: 22, fontFamily: 'Manrope, sans-serif' }}>Import offerings</div>
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 16 }}>
          {['Upload', 'Map columns', 'Preview', 'Import'].map((label, i) => (
            <div key={label} style={{ fontSize: 12, color: i + 1 <= step ? 'var(--text)' : 'var(--text3)', borderBottom: `2px solid ${i + 1 <= step ? 'var(--accent)' : 'var(--border)'}`, paddingBottom: 6 }}>
              {i + 1}. {label}
            </div>
          ))}
        </div>

        {step === 1 ? (
          <div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                <button
                  type="button"
                  onClick={() => setGuideOpen(o => !o)}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text2)', fontSize: 13 }}
                >
                  <span style={{ fontSize: 10, display: 'inline-block', transform: guideOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }}>▶</span>
                  Column guide
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ fontSize: 12, padding: '4px 12px', whiteSpace: 'nowrap' }}
                  onClick={downloadTemplate}
                >
                  Download template
                </button>
              </div>

              {guideOpen ? (
                <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 12 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: 'var(--bg2)' }}>
                        <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600, width: 160 }}>Column</th>
                        <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600, width: 80 }}>Required</th>
                        <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600 }}>Description</th>
                        <th style={{ padding: '7px 12px', textAlign: 'left', color: 'var(--text2)', fontWeight: 600, width: 200 }}>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { col: 'name', req: true, desc: 'Name of the service or product. Existing entries matched by name will be updated.', ex: 'Haircut & Style, Moroccan Oil Shampoo' },
                        { col: 'type', req: false, desc: 'Offering type: "service" or "product". Defaults to service if blank or column is omitted.', ex: 'service, product' },
                        { col: 'description', req: false, desc: 'Short description shown to staff and used for AI context.', ex: 'Full haircut and blow-dry' },
                        { col: 'price', req: true, desc: 'Price — no currency symbol.', ex: '65.00' },
                        { col: 'duration_minutes', req: false, desc: 'Duration in minutes. Applies to services only — ignored for products.', ex: '60' },
                      ].map((row, i) => (
                        <tr key={row.col} style={{ borderTop: i === 0 ? undefined : '1px solid var(--border)', background: i % 2 === 0 ? undefined : 'var(--bg2)' }}>
                          <td style={{ padding: '7px 12px', fontFamily: "'DM Mono', monospace", fontSize: 11 }}>{row.col}</td>
                          <td style={{ padding: '7px 12px' }}>
                            {row.req
                              ? <span className="pill pill-lapsed">required</span>
                              : <span style={{ color: 'var(--text3)' }}>optional</span>}
                          </td>
                          <td style={{ padding: '7px 12px', color: 'var(--text2)' }}>{row.desc}</td>
                          <td style={{ padding: '7px 12px', fontFamily: "'DM Mono', monospace", fontSize: 11, color: 'var(--text3)' }}>{row.ex}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </div>

            <div
              role="button"
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInputRef.current?.click() }
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={e => { e.preventDefault(); e.stopPropagation(); dragDepthRef.current += 1; setDropActive(true) }}
              onDragLeave={e => { e.preventDefault(); e.stopPropagation(); dragDepthRef.current = Math.max(0, dragDepthRef.current - 1); if (dragDepthRef.current === 0) setDropActive(false) }}
              onDragOver={e => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'copy' }}
              onDrop={e => {
                e.preventDefault(); e.stopPropagation()
                dragDepthRef.current = 0; setDropActive(false)
                const file = e.dataTransfer.files?.[0]
                if (!file) return
                if (!isCsvFile(file)) { setError('Please drop a .csv file.'); return }
                setError(null); void handleFile(file)
              }}
              style={{
                display: 'grid', placeItems: 'center',
                border: `2px dashed ${dropActive ? 'var(--accent)' : 'var(--border2)'}`,
                borderRadius: 12, minHeight: 160, cursor: 'pointer',
                background: dropActive ? 'var(--accent-bg)' : undefined,
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <div style={{ textAlign: 'center', pointerEvents: 'none' }}>
                <div style={{ color: 'var(--text2)', marginBottom: 6 }}>Drop CSV here or click to upload</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Services and products can be mixed in one file.</div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) void handleFile(f); e.target.value = '' }}
              />
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div>
            <div style={{ marginBottom: 10, fontSize: 12, color: 'var(--text2)' }}>{fileName} · {rows.length} rows</div>
            <table className="data-table">
              <thead>
                <tr><th>Your column</th><th>Maps to</th><th>Type</th><th>Status</th></tr>
              </thead>
              <tbody>
                {columns.map(col => {
                  const mapped = mapping[col] ?? 'skip'
                  const isRequired = REQUIRED_TARGETS.includes(mapped as (typeof REQUIRED_TARGETS)[number])
                  return (
                    <tr key={col}>
                      <td style={{ fontFamily: "'DM Mono', monospace" }}>{col}</td>
                      <td>
                        <select className="select-input" value={mapped} onChange={e => setMapping(prev => ({ ...prev, [col]: e.target.value }))}>
                          {TARGET_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      </td>
                      <td>{isRequired ? <span className="pill pill-lapsed">required</span> : <span className="pill pill-pending">optional</span>}</td>
                      <td>{mapped === 'skip' ? <span style={{ color: 'var(--text3)' }}>skipped</span> : <span className="pill pill-replied">matched</span>}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            {requiredMissing.length > 0 ? (
              <div style={{ marginTop: 10, color: 'var(--orange)' }}>
                Missing required mappings: {requiredMissing.join(', ')}
              </div>
            ) : null}
          </div>
        ) : null}

        {step === 3 ? (() => {
          const allRows = preview?.rows ?? []
          const invalidRows = allRows.filter(r => r.rowWarnings.length > 0)
          const validCount = allRows.length - invalidRows.length
          return (
            <div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: 'var(--text2)' }}>{allRows.length} rows total</span>
                <span className="pill pill-replied">{validCount} will import</span>
                {invalidRows.length > 0
                  ? <span className="pill pill-lapsed">{invalidRows.length} will be skipped</span>
                  : null}
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid var(--border)', borderRadius: 10, marginBottom: 12 }}>
                <table className="data-table" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}>#</th>
                      <th>Name</th>
                      <th style={{ width: 80 }}>Type</th>
                      <th>Description</th>
                      <th style={{ textAlign: 'right', width: 80 }}>Price</th>
                      <th style={{ textAlign: 'right', width: 90 }}>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allRows.map(r => {
                      const hasIssue = r.rowWarnings.length > 0
                      const isProduct = r.offeringType === 'product'
                      return (
                        <React.Fragment key={r.rowNumber}>
                          <tr style={hasIssue ? { background: 'var(--orange-bg)' } : undefined}>
                            <td style={{ color: 'var(--text3)', fontSize: 11 }}>{r.rowNumber}</td>
                            <td style={{ fontWeight: 500 }}>{r.name || <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                            <td>
                              <span className={isProduct ? 'pill pill-pending' : 'pill pill-sent'}>
                                {r.offeringType}
                              </span>
                            </td>
                            <td style={{ color: 'var(--text2)', fontSize: 12 }}>{r.description ?? <span style={{ color: 'var(--text3)' }}>—</span>}</td>
                            <td style={{ textAlign: 'right', fontFamily: "'DM Mono', monospace", fontSize: 12 }}>
                              {r.price != null ? `$${r.price}` : <span style={{ color: 'var(--text3)' }}>—</span>}
                            </td>
                            <td style={{ textAlign: 'right', fontSize: 12, color: 'var(--text2)' }}>
                              {r.durationMinutes != null
                                ? `${r.durationMinutes} min`
                                : <span style={{ color: 'var(--text3)' }}>{isProduct ? 'n/a' : '—'}</span>}
                            </td>
                          </tr>
                          {hasIssue ? (
                            <tr style={{ background: 'var(--orange-bg)' }}>
                              <td />
                              <td colSpan={5} style={{ paddingTop: 0, paddingBottom: 6 }}>
                                {r.rowWarnings.map(w => (
                                  <span key={w} style={{ display: 'inline-block', fontSize: 11, color: 'var(--orange)', marginRight: 10 }}>
                                    ⚠ {w}
                                  </span>
                                ))}
                              </td>
                            </tr>
                          ) : null}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })() : null}

        {step === 4 ? (
          <div style={{ padding: '20px 4px' }}>
            <div style={{ fontSize: 16, marginBottom: 12 }}>Import complete</div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
              <span className="pill pill-replied">{result?.importedCount ?? 0} imported</span>
              {(result?.skippedCount ?? 0) > 0
                ? <span className="pill pill-lapsed">{result?.skippedCount} skipped</span>
                : null}
            </div>
            {(result?.errors?.length ?? 0) > 0 ? (
              <div style={{ background: 'var(--orange-bg)', border: '1px solid var(--orange-bd)', borderRadius: 10, padding: '10px 14px' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--orange)', marginBottom: 8 }}>Skipped rows</div>
                <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                  {(result?.errors ?? []).map((e, i) => (
                    <li key={i} style={{ fontSize: 12, color: 'var(--orange)', marginBottom: 4 }}>{e}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? <div style={{ marginTop: 10, color: 'var(--red)' }}>{error}</div> : null}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 16 }}>
          <div>
            {step > 1 ? (
              <button type="button" className="btn-secondary" disabled={busy} onClick={goBack}>Back</button>
            ) : null}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            {step === 2 ? (
              <button type="button" className="btn-primary" disabled={busy || requiredMissing.length > 0} onClick={runPreview}>
                Continue to preview
              </button>
            ) : null}
            {step === 3 ? (
              <button type="button" className="btn-primary" disabled={busy || !preview?.valid} onClick={runImport}>
                Import offerings
              </button>
            ) : null}
            {step === 4 ? (
              <button type="button" className="btn-primary" onClick={onClose}>Done</button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
